import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AlertModal } from "stories/components";

// In-app replacement for window.confirm and window.alert.
//
// confirm() asks the user to proceed (Vahvista / Peruuta) and resolves true or false.
// notify() shows a notice (OK) and resolves when the user dismisses it.
// Render `dialog` from the same component that calls confirm or notify, including
// every early return, because the dialog is part of that component's tree.
// Form screens keep using stories/components Modal. This hook uses AlertModal.
export function useAppDialog() {
  const { t } = useTranslation();
  const resolverRef = useRef(null);
  const [dialog, setDialog] = useState(null);

  const finish = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    if (resolve) {
      resolve(result);
    }
  }, []);

  const open = useCallback((resolve, alert) => {
    if (resolverRef.current) {
      resolverRef.current(false);
    }
    resolverRef.current = resolve;
    setDialog(alert);
  }, []);

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      open(
        resolve,
        <AlertModal
          title={t("app_dialog_confirm_title", "Vahvista")}
          showCancel
          warning
          confirmBtnBsStyle="danger"
          confirmBtnText={t("app_dialog_confirm", "Vahvista")}
          cancelBtnText={t("app_dialog_cancel", "Peruuta")}
          onConfirm={() => finish(true)}
          onCancel={() => finish(false)}
        >
          {message}
        </AlertModal>
      );
    });
  }, [t, finish, open]);

  const notify = useCallback((message) => {
    return new Promise((resolve) => {
      open(
        resolve,
        <AlertModal
          title={t("app_dialog_notice_title", "Huomio")}
          info
          confirmBtnText={t("app_dialog_ok", "OK")}
          onConfirm={() => finish(undefined)}
          onCancel={() => finish(undefined)}
        >
          {message}
        </AlertModal>
      );
    });
  }, [t, finish, open]);

  const dialogNode = dialog && typeof document !== "undefined"
    ? createPortal(dialog, document.body)
    : dialog;

  return { dialog: dialogNode, confirm, notify };
}
